export function formatTime(dt: Date | null): string {
  if (!dt) return "";
  return `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
}

export function formatDurationMinutes(minutes: number): string {
  if (minutes <= 0) return "–";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function formatDurationTitle(minutes: number, language: string): string {
  const isHours = minutes >= 60;
  return new Intl.NumberFormat(language, {
    style: "unit",
    unit: isHours ? "hour" : "minute",
    unitDisplay: "long",
  }).format(isHours ? minutes / 60 : minutes);
}

export const getNameInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .reduce(
      (initials, part) =>
        initials.length < 2 ? initials + part.charAt(0).toUpperCase() : initials,
      ""
    );

export const formatSlackHandle = (handle: string): string =>
  handle.startsWith("@") ? handle : `@${handle}`;

import i18n from "@/i18n.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ApiPartialDate, Rounding } from "@/types/common.types.ts";
import { ApiEmployeeShort, EmployeeFullNameRenderType } from "@/types/employees/employee.types.ts";
import { ApiLocation } from "@/types/location.types.ts";
import {
  differenceInBusinessDays,
  endOfMonth,
  format,
  intervalToDuration,
  isToday,
  isWeekend,
  isYesterday,
  formatDistanceToNow,
} from "date-fns";
import { enUS, ru } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import { useTranslation } from "react-i18next";

export interface PeriodDatesFormatted {
  title: string;
  tooltip: string;
}

export default function useFormatter() {
  const { data: employee } = useEmployeeMe();

  const { t } = useTranslation();

  function _getLocale() {
    if (employee?.language == undefined) {
      return enUS;
    }

    if (employee.language == "ru") return ru;
    return enUS;
  }

  function renderEmployeeName(emp: ApiEmployeeShort) {
    if (emp.display_name !== undefined) return emp.display_name;

    const formatter: EmployeeFullNameRenderType =
      employee?.organization.employee_name_format || EmployeeFullNameRenderType.first_last;
    switch (formatter) {
      case EmployeeFullNameRenderType.first_last:
        return `${emp.first_name} ${emp.last_name}`;
      case EmployeeFullNameRenderType.last_first:
        return `${emp.last_name} ${emp.first_name}`;
      case EmployeeFullNameRenderType.f_last:
        return `${emp.first_name.charAt(0)}. ${emp.last_name}`;
      default:
        return `${emp.first_name} ${emp.last_name}`;
    }
  }

  function renderDateAndTime(date: Date) {
    return format(date, "PPp", { locale: _getLocale() });
  }

  function renderDate(date: Date, dateFormat?: string) {
    return format(date, dateFormat || "PP", { locale: _getLocale() });
  }

  function renderTime(date: Date, timezone?: string) {
    return timezone
      ? formatInTimeZone(date, timezone, "p", { locale: _getLocale() })
      : format(date, "p", { locale: _getLocale() });
  }

  function formatPeriodDates({
    dateStart,
    dateEnd,
  }: {
    dateStart?: Date;
    dateEnd?: Date;
  }): PeriodDatesFormatted | null {
    if (!dateStart || !dateEnd) return null;

    const isTheSameYear = dateStart.getFullYear() === dateEnd.getFullYear();

    const isTheSameMonth = dateStart.getMonth() === dateEnd.getMonth();
    const isTheSameDay = isTheSameMonth && dateStart.getDate() === dateEnd.getDate();

    const isCurrentYear =
      dateStart.getFullYear() == dateEnd.getFullYear() &&
      dateEnd.getFullYear() === new Date().getFullYear();

    const fullPeriod = `${format(dateStart, "dd MMMM yyyy", { locale: _getLocale() })} — ${format(dateEnd, "dd MMMM yyyy", { locale: _getLocale() })}`;

    if (isCurrentYear) {
      if (isTheSameDay) {
        return {
          title: format(dateStart, "d MMM", { locale: _getLocale() }),
          tooltip: fullPeriod,
        };
      } else if (isTheSameMonth) {
        if (dateStart.getDate() === 1 && dateEnd.getDate() === endOfMonth(dateEnd).getDate()) {
          return {
            title: `${capitalizeFirstLetter(format(dateStart, "LLL yy", { locale: _getLocale() }))}`,
            tooltip: fullPeriod,
          };
        }
        return {
          title: `${format(dateStart, "d", { locale: _getLocale() })} — ${format(dateEnd, "d, MMM", { locale: _getLocale() })}`,
          tooltip: fullPeriod,
        };
      } else {
        return {
          title: `${format(dateStart, "d MMM", { locale: _getLocale() })} — ${format(dateEnd, "d MMM", { locale: _getLocale() })}`,
          tooltip: fullPeriod,
        };
      }
    } else {
      if (isTheSameYear) {
        if (isTheSameDay) {
          return {
            title: format(dateStart, "d MMM yyyy", { locale: _getLocale() }),
            tooltip: fullPeriod,
          };
        } else if (isTheSameMonth) {
          if (dateStart.getDate() === 1 && dateEnd.getDate() === endOfMonth(dateEnd).getDate()) {
            return {
              title: `${capitalizeFirstLetter(format(dateStart, "LLL yy", { locale: _getLocale() }))}`,
              tooltip: fullPeriod,
            };
          }
          return {
            title: `${format(dateStart, "d", { locale: _getLocale() })} — ${format(dateEnd, "d, MMM yyyy", { locale: _getLocale() })}`,
            tooltip: fullPeriod,
          };
        } else {
          return {
            title: `${format(dateStart, "d MMM", { locale: _getLocale() })} — ${format(dateEnd, "d MMM, yyyy", { locale: _getLocale() })}`,
            tooltip: fullPeriod,
          };
        }
      } else {
        return {
          title: `${format(dateStart, "d MMM yyyy", { locale: _getLocale() })} — ${format(dateEnd, "d MMM yyyy", { locale: _getLocale() })}`,
          tooltip: fullPeriod,
        };
      }
    }
  }

  function renderRelativeDate(date: Date, showTime: boolean = true) {
    const timePart = showTime ? `, ${format(date, "p", { locale: _getLocale() })}` : "";

    if (isToday(date)) {
      return `${i18n.t("common:today")}${timePart}`;
    }
    if (isYesterday(date)) {
      return `${i18n.t("common:yesterday")}${timePart}`;
    }
    return renderDate(date);
  }

  function renderTimeAgo(date: Date) {
    return formatDistanceToNow(date, { addSuffix: true, locale: _getLocale() });
  }

  function renderTimeAgoShort(date: Date) {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    let time: string;
    if (diffYears > 0) {
      time = t("common:years_short", { count: diffYears });
    } else if (diffMonths > 0) {
      time = t("common:months_short", { count: diffMonths });
    } else if (diffWeeks > 0) {
      time = t("common:weeks_short", { count: diffWeeks });
    } else if (diffDays > 0) {
      time = t("common:days_short", { count: diffDays });
    } else if (diffHours > 0) {
      time = t("common:hours_short", { count: diffHours });
    } else if (diffMin > 0) {
      time = t("common:minutes_short", { count: diffMin });
    } else {
      return t("common:now");
    }

    return t("common:time_ago", { time });
  }

  function renderPartialDate(date: ApiPartialDate | undefined): string | undefined {
    if (!date) return undefined;

    if (date.month && date.day && date.year) {
      const birthDate = new Date(date.year, date.month - 1, date.day);

      return renderDate(birthDate);
    }

    if (date.month && date.day) {
      return format(new Date(2000, date.month - 1, date.day), "dd MMM", { locale: _getLocale() });
    }

    return undefined;
  }

  function renderShortDate(date: Date | undefined) {
    if (!date) return undefined;
    return format(date, "dd MMM", { locale: _getLocale() });
  }

  function buildFromPartialDate(date: ApiPartialDate | undefined): Date | undefined {
    if (!date) return undefined;

    if (date.month && date.day && date.year) {
      return new Date(date.year, date.month - 1, date.day);
    }

    return undefined;
  }

  function calculateAge(date: ApiPartialDate | undefined): number {
    if (!date) return 0;

    const currentDate = new Date();
    const birthDate = new Date();

    if (!date.year) return 0;

    birthDate.setFullYear(date.year);
    birthDate.setMonth(date.month ? date.month - 1 : 0);

    birthDate.setDate(date.day ?? 1);

    if (birthDate > currentDate) return 0;

    const age = currentDate.getFullYear() - birthDate.getFullYear();

    if (
      birthDate.getMonth() > currentDate.getMonth() ||
      (birthDate.getMonth() === currentDate.getMonth() &&
        birthDate.getDate() > currentDate.getDate())
    ) {
      return age - 1;
    }

    return age;
  }

  function capitalizeFirstLetter(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  function formatDate(date: ApiPartialDate): string {
    let result = "";

    if (date.year && date.month && date.day) {
      result += `${capitalizeFirstLetter(format(new Date(date.year, date.month - 1, date.day), "LLLL", { locale: _getLocale() }))} ${date.year}`;
    } else if (date.year && date.month) {
      result += `${capitalizeFirstLetter(format(new Date(date.year, date.month - 1, 1), "LLLL", { locale: _getLocale() }))} ${date.year}`;
    } else if (date.year) {
      result += `${date.year}`;
    }

    return result;
  }

  function formatDuration(from: ApiPartialDate, to: ApiPartialDate | null | undefined): string {
    let fromString: string;
    let toString: string;

    if (from && from.year) {
      fromString = formatDate(from);
    } else {
      fromString = "";
    }

    if (to && to.year) {
      toString = formatDate(to);
    } else {
      toString = t("common:present_time");
    }

    return `${fromString.length > 0 ? `${fromString} - ` : ``}${toString}`;
  }

  function formatDurationDatetime(from: Date, to: Date): string {
    const totalSeconds = Math.floor((to.getTime() - from.getTime()) / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${t("common:hours_short", { count: hours })} ${t("common:minutes_short", { count: minutes })}`;
    }
    if (minutes > 0) {
      return `${t("common:minutes_short", { count: minutes })} ${t("common:seconds_short", { count: seconds })}`;
    }
    return t("common:seconds_short", { count: seconds });
  }

  function formatExperiencePeriod(
    date_from: ApiPartialDate,
    date_to: ApiPartialDate | undefined
  ): string {
    const duration = formatExperienceDuration(date_from, date_to);

    return duration
      ? `${formatDuration(date_from, date_to)} (${duration})`
      : formatDuration(date_from, date_to);
  }

  function formatExperienceDuration(
    date_from: ApiPartialDate,
    date_to: ApiPartialDate | undefined
  ): string {
    const today = new Date();
    let duration = "";

    if (date_from && date_from.year) {
      const from = new Date(date_from.year || 0, (date_from.month || 0) - 1, date_from.day || 1);
      const to =
        date_to && date_to.year
          ? new Date(date_to.year || 0, (date_to.month || 0) - 1, date_to.day || 1)
          : today;
      const interval = intervalToDuration({
        start: from,
        end: to,
      });

      const durationArray: string[] = [];

      // Считаем, что если он работал 11 месяцев и 5 дней, то это 1 год
      const days = interval.days || 0;
      let months = interval.months ? interval.months + (days > 0 ? 1 : 0) : 0;
      let years = interval.years || 0;
      if (months >= 12) {
        months -= 12;
        years += 1;
      }

      if (years > 0) {
        durationArray.push(i18n.t("plurals:plural_year", { count: years }));
      }

      if (months > 0) {
        durationArray.push(i18n.t("plurals:plural_month", { count: months }));
      }

      duration = durationArray.join(" ");
    }

    return duration;
  }

  function formatDurationPartialDate(
    date_from: ApiPartialDate,
    date_to: ApiPartialDate | undefined,
    onlyYears: boolean = false,
    includeBothDates: boolean = false
  ): string {
    const today = new Date();
    let duration = "";

    if (date_from && date_from.year) {
      const from = new Date(date_from.year || 0, (date_from.month || 0) - 1, date_from.day || 1);
      const to =
        date_to && date_to.year
          ? new Date(date_to.year || 0, (date_to.month || 0) - 1, date_to.day || 1)
          : today;
      const interval = intervalToDuration({
        start: from,
        end: to,
      });

      const durationArray: string[] = [];

      const months = onlyYears ? 0 : interval.months || 0;
      const years = interval.years || 0;
      const days = (interval.days || 0) + (includeBothDates ? 1 : 0);

      if (years > 0) {
        durationArray.push(i18n.t("plurals:plural_year", { count: years }));
      }

      if (months > 0) {
        durationArray.push(i18n.t("plurals:plural_month", { count: months }));
      }

      if (durationArray.length == 0) {
        durationArray.push(i18n.t("plurals:plural_day", { count: days }));
      }

      duration = durationArray.join(" ");
    }

    return duration;
  }

  function transformDateToPartialDate(date: Date): ApiPartialDate {
    return {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
  }

  function renderFileSize(bytes: number, si = false, dp = 1) {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
      return bytes + " B";
    }

    const units = si
      ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
      : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
    let u = -1;
    const r = 10 ** dp;

    do {
      bytes /= thresh;
      ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return bytes.toFixed(dp) + " " + units[u];
  }

  function renderLocationAddress(location: ApiLocation) {
    let address = "";
    if (location.address_1) {
      address += location.address_1 + "\n";
    }
    if (location.address_2) {
      address += location.address_2 + "\n";
    }

    const city = `${location.city || ""} ${location.state || ""} ${location.zip || ""}`.trim();
    if (city) {
      address += city + "\n";
    }

    address += location.country.title;
    return address;
  }

  function getDaysToDate(date?: Date, rounding: Rounding = Rounding.down) {
    if (!date) return 0;

    if (new Date() > date) return 0;

    const now = new Date();
    const day = 86400000;

    switch (rounding) {
      case Rounding.down:
        return Math.floor(Math.abs((date.getTime() - now.getTime()) / day));
      case Rounding.up:
        return Math.ceil(Math.abs((date.getTime() - now.getTime()) / day));
      case Rounding.usual:
        return Math.round(Math.abs((date.getTime() - now.getTime()) / day));
    }
  }

  function getOverdueDays(date?: Date): number {
    if (!date) return 0;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);

    const day = 86400000;
    const diff = Math.floor((now.getTime() - target.getTime()) / day);

    return diff > 0 ? diff : 0;
  }

  const calculateBusinessDays = (dateFrom: Date, dateTo: Date) => {
    // Add 1 to include both start and end dates
    const diff = differenceInBusinessDays(dateTo, dateFrom) + 1;

    if (dateFrom.getTime() === dateTo.getTime()) {
      return isWeekend(dateFrom) ? 0 : 1;
    }

    return Math.max(0, diff);
  };

  const renderShortPeriod = (dateFrom: Date, dateTo: Date) => {
    const fromYear = dateFrom.getFullYear();
    const toYear = dateTo.getFullYear();
    const currentYear = new Date().getFullYear();

    if (fromYear !== toYear) {
      return `${renderShortDate(dateFrom)} ${fromYear} – ${renderShortDate(dateTo)} ${toYear}`;
    }

    const yearSuffix = fromYear !== currentYear ? ` ${fromYear}` : "";

    if (dateFrom.getMonth() !== dateTo.getMonth()) {
      return `${renderShortDate(dateFrom)} – ${renderShortDate(dateTo)}${yearSuffix}`;
    }

    return `${dateFrom.getDate() < 10 ? `0${dateFrom.getDate()}` : dateFrom.getDate()} – ${renderShortDate(dateTo)}${yearSuffix}`;
  };

  const renderMonthYear = (date: Date) => {
    return capitalizeFirstLetter(format(date, "LLL yyyy", { locale: _getLocale() }));
  };

  const translitMap: Record<string, string> = {
    а: "a",
    б: "b",
    в: "v",
    г: "g",
    д: "d",
    е: "e",
    ё: "yo",
    ж: "zh",
    з: "z",
    и: "i",
    й: "y",
    к: "k",
    л: "l",
    м: "m",
    н: "n",
    о: "o",
    п: "p",
    р: "r",
    с: "s",
    т: "t",
    у: "u",
    ф: "f",
    х: "kh",
    ц: "ts",
    ч: "ch",
    ш: "sh",
    щ: "sch",
    ъ: "",
    ы: "y",
    ь: "",
    э: "e",
    ю: "yu",
    я: "ya",
  };

  const getSlug = (text: string) => {
    return text
      .toLowerCase()
      .split("")
      .map((char) => {
        if (translitMap[char]) {
          return translitMap[char];
        }
        if (/[a-z0-9]/.test(char)) {
          return char;
        }
        if (/[\s-]/.test(char)) {
          return "-";
        }
        return "";
      })
      .join("")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const renderCurrency = (value: string, currency: string | null) => {
    let locale = "en";
    if (employee && employee.language == "ru") locale = "ru";

    const formatted = Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(parseFloat(value));

    if (!currency) return formatted;

    try {
      return Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(parseFloat(value));
    } catch {
      return `${formatted} ${currency.toUpperCase()}`;
    }
  };

  const stripHtml = (html: string): string => {
    if (!html) return "";

    let text = html.replace(
      /<\/?(div|p|h[1-6]|ul|ol|li|blockquote|pre|table|tr|td|th|section|article|header|footer|nav|aside|main|figure|figcaption|hr|br)[^>]*>/g,
      "\n"
    );

    text = text.replace(/<[^>]*>?/g, "");

    return text.replace(/^\s+|\s+$/g, "");
  };

  const roundPercent = (percent?: number | null): number => {
    return percent ? Math.round(percent * 10000) / 100 : 0;
  };

  const numberToFixed = (value: number | string | null | undefined): number => {
    if (value === null || value === undefined) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : Number(num.toFixed(2));
  };

  const formatMonthName = (date: Date) => {
    return format(date, "MMMM", { locale: _getLocale() });
  };

  const truncate = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "…";
  };

  return {
    calculateAge,
    renderTime,
    renderEmployeeName,
    renderDate,
    formatExperiencePeriod,
    renderPartialDate,
    renderShortDate,
    renderRelativeDate,
    renderTimeAgo,
    renderTimeAgoShort,
    renderDateAndTime,
    renderFileSize,
    transformDateToPartialDate,
    formatDurationPartialDate,
    formatDurationDatetime,
    renderLocationAddress,
    getDaysToDate,
    getOverdueDays,
    buildFromPartialDate,
    calculateBusinessDays,
    renderShortPeriod,
    renderMonthYear,
    capitalizeFirstLetter,
    formatPeriodDates,
    formatDate,
    getSlug,
    renderCurrency,
    stripHtml,
    roundPercent,
    numberToFixed,
    formatMonthName,
    truncate,
  };
}
